import { useCallback, useState } from 'react';

/**
 * Custom hook to manage image viewer modal state and functionality
 * @returns {Object} Image viewer state and handlers
 */
const useImageViewer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  /**
   * Opens the image viewer modal at a specific image index
   * @param {number} index - Index of the image to display
   */
  const openImageViewer = useCallback((index = 0) => {
    setCurrentImageIndex(index);
    setIsOpen(true);
  }, []);

  /**
   * Closes the image viewer modal
   */
  const closeImageViewer = useCallback(() => {
    setIsOpen(false);
  }, []);

  /**
   * Navigates to the next image
   * @param {number} totalImages - Total number of images available
   */
  const goToNext = useCallback((totalImages) => {
    setCurrentImageIndex((prev) => (prev + 1) % totalImages);
  }, []);

  /**
   * Navigates to the previous image
   * @param {number} totalImages - Total number of images available
   */
  const goToPrevious = useCallback((totalImages) => {
    setCurrentImageIndex((prev) => (prev - 1 + totalImages) % totalImages);
  }, []);

  /**
   * Sets a specific image index
   * @param {number} index - Index to set as current
   */
  const setImageIndex = useCallback((index) => {
    setCurrentImageIndex(index);
  }, []);

  /**
   * Handles image click event
   * @param {number} index - Index of the clicked image
   */
  const handleImageClick = useCallback(
    (index) => {
      openImageViewer(index);
    },
    [openImageViewer]
  );

  return {
    // State
    isOpen,
    currentImageIndex,

    // Actions
    openImageViewer,
    closeImageViewer,
    goToNext,
    goToPrevious,
    setImageIndex,
    handleImageClick,
  };
};

export default useImageViewer;
